import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, ArrowRight } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import { PricingSchema, BreadcrumbSchema } from '../components/StructuredData';

const tiers = [
  {
    name: 'Starter',
    prices: [
      { currency: 'INR', amount: '35K-50K', period: '/mo' },
      { currency: 'AED', amount: '2,500', period: '/mo' },
      { currency: 'USD', amount: '800', period: '/mo' },
    ],
    description: 'For early-stage companies with straightforward financials.',
    txnLimit: 'Upto 500 transactions',
    features: [
      'Upto 500 transactions/month',
      '2 bank accounts',
      '1 payment gateway',
      'Monthly close',
      'Basic GST/TDS filings',
      'Cash & burn tracking',
      'Monthly financial digest',
    ],
    highlight: false,
  },
  {
    name: 'Growth',
    prices: [
      { currency: 'INR', amount: '60K-1.2L', period: '/mo' },
      { currency: 'AED', amount: '3,500-4,000', period: '/mo' },
      { currency: 'USD', amount: '1,200-1,500', period: '/mo' },
    ],
    description: 'For scaling companies with multi-channel operations.',
    txnLimit: '500-3,000 transactions',
    features: [
      '500-3,000 transactions/month',
      'Multi-gateway & e-commerce',
      'Inventory management',
      'PO 3-way match',
      'T+1 posting SLA',
      'Daily bank reconciliation',
      'AR/AP aging & follow-ups',
      'Quarterly CFO review',
    ],
    highlight: true,
  },
  {
    name: 'Scale',
    prices: [
      { currency: '', amount: 'Custom', period: '' },
    ],
    description: 'For multi-entity companies with complex requirements.',
    txnLimit: '3,000+ transactions',
    features: [
      '3,000+ transactions/month',
      'Multi-entity consolidation',
      'FX reconciliation',
      'Advanced FP&A',
      'Dedicated CFO hours',
      'Board packs & investor reporting',
      'Cross-border compliance',
      'Custom SLA terms',
    ],
    highlight: false,
  },
];

export default function PricingPage() {
  return (
    <div>
      <SEO
        title="Pricing - Transparent Plans"
        description="Transparent pricing for daily bookkeeping, compliance, and CFO services. Plans from INR 35K/AED 2,500/USD 800 per month. Starter, Growth, and Scale tiers."
        path="/pricing"
      />
      <PricingSchema plans={[
        { name: 'Starter', description: 'Up to 500 transactions/month. Daily bookkeeping + basic compliance.', price: '35000' },
        { name: 'Growth', description: '500-3000 transactions/month. Multi-gateway, inventory, quarterly CFO review.', price: '60000' },
        { name: 'Scale', description: '3000+ transactions. Multi-entity consolidation, dedicated CFO, custom SLA.', price: '0' },
      ]} />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }, { name: 'Pricing', path: '/pricing' }]} />

      {/* Hero */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-6">Pricing</p>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-none text-black mb-6">
            Transparent pricing.<br />No surprises.
          </h1>
          <p className="text-base text-gray-600 max-w-2xl">
            Choose a plan that matches your transaction volume and complexity. All plans include daily bookkeeping and compliance coverage.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section data-testid="pricing-section" className="bg-[#F9FAFB] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black/10">
            {tiers.map((tier, i) => (
              <div
                key={i}
                data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
                className={`p-8 md:p-10 bg-white ${
                  tier.highlight ? 'border-2 border-[#002FA7] relative' : ''
                } ${i < 2 ? 'border-b md:border-b-0 md:border-r border-black/10' : ''}`}
              >
                {tier.highlight && (
                  <span className="absolute -top-3 left-8 bg-[#002FA7] text-white text-xs font-bold px-3 py-1 uppercase tracking-wider">
                    Most Popular
                  </span>
                )}
                <h3 className="text-xl font-bold tracking-tight text-black mb-2">{tier.name}</h3>
                <p className="text-xs text-gray-500 mb-4">{tier.description}</p>
                <p className="text-xs text-[#002FA7] font-semibold uppercase tracking-wider mb-4">{tier.txnLimit}</p>
                <div className="mb-6 space-y-1">
                  {tier.prices.map((p, j) => (
                    <div key={j} className="flex items-baseline gap-2">
                      {p.currency && <span className="text-xs font-semibold text-gray-500 w-8">{p.currency}</span>}
                      <span className={`font-black text-black ${j === 0 ? 'text-2xl' : 'text-lg'}`}>{p.amount}</span>
                      {p.period && <span className="text-sm text-gray-500">{p.period}</span>}
                    </div>
                  ))}
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check size={16} weight="bold" className="text-[#002FA7] flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/#lead-form">
                  <Button
                    data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                    className={`w-full rounded-[4px] text-sm font-semibold ${
                      tier.highlight
                        ? 'bg-[#002FA7] hover:bg-[#002277] text-white'
                        : 'bg-white border border-black text-black hover:bg-black hover:text-white'
                    }`}
                  >
                    Get Started <ArrowRight size={14} className="ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black mb-8">Add-ons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-black/10">
            {[
              { title: 'Fractional CFO Hours', desc: 'On-demand strategic CFO time' },
              { title: 'Audit Prep', desc: 'Per engagement audit preparation' },
              { title: 'Payroll Services', desc: 'Full payroll processing & compliance' },
              { title: 'US/UAE Tax', desc: 'Inhouse CPAs' },
            ].map((a, i) => (
              <div key={i} className={`p-6 md:p-8 ${i < 3 ? 'border-b md:border-b-0 md:border-r border-black/10' : ''}`}>
                <h4 className="text-sm font-bold text-black mb-1">{a.title}</h4>
                <p className="text-xs text-gray-500">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Included */}
      <section className="bg-[#F9FAFB] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black mb-8">What's included in every plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              'T+1 posting for all cleared transactions',
              'Less than 24h response on exceptions',
              '>98% classification accuracy (after month 2)',
              'Support hours: 8 AM - 8 PM India/UAE',
              'USA support: 6 PM - 3 AM IST',
              'Dedicated intake email & WhatsApp',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <Check size={18} weight="bold" className="text-[#00C853] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">Start with a free health check</h2>
          <p className="text-gray-400 mb-8 text-sm">No commitment. Keep everything we set up even if you leave.</p>
          <Link to="/#lead-form">
            <Button data-testid="pricing-page-cta" className="bg-[#002FA7] hover:bg-[#002277] text-white rounded-[4px] px-8 py-3 text-sm font-semibold">
              Get Free Finance Health Check <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
